export const emptyGlobalFilters = () => ({
  timePeriod: "",
  locations: [] as any[],
  departments: [] as any[],
});

export const emptyCrFilters = () => ({
  taskStatus: [] as any[],
  taskType: [] as any[],
  control: "",
  controlPriority: [] as any[],
  controlFrequency: [] as any[],
  framework: [] as any[],
  tier: "",
  activity: [] as any[],
  system: [] as any[],
  project: [] as any[],
});

export const emptyFindingsFilters = () => ({
  findings: [] as any[],
  taskStatus: [] as any[],
});

export const emptyKpiFilters = () => ({
  kpiId: "",
  timePeriod: "",
  teams: [] as any[],
});

export const emptyControlFilters = () => ({
  control: "",
  timePeriod: "",
  teams: [] as any[],
});

export const isEmptyFilterValue = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
};

export const pickNonEmpty = (filters: Record<string, any> = {}) => {
  const result: Record<string, any> = {};
  Object.keys(filters || {}).forEach((key) => {
    if (!isEmptyFilterValue(filters[key])) {
      result[key] = filters[key];
    }
  });
  return result;
};

export const cloneFilters = <T>(filters: T): T => JSON.parse(JSON.stringify(filters || {}));

export const clearedFilterValue = (value: any) => (Array.isArray(value) ? [] : "");

export const countActiveFilters = (filters: Record<string, any> = {}) =>
  Object.keys(pickNonEmpty(filters)).length;

export const clearFilterKey = (filters: Record<string, any>, key: string) => ({
  ...filters,
  [key]: clearedFilterValue(filters[key]),
});

export const mergeFilters = (globalFilters: any = {}, sectionFilters: any = {}) => ({
  ...pickNonEmpty(globalFilters),
  ...pickNonEmpty(sectionFilters),
});

const optionValue = (item: any) => {
  if (item && typeof item === "object") {
    return item.value ?? item.id ?? item._id ?? item.label;
  }
  return item;
};

export const normalizeFilterPayload = (filters: any = {}) => {
  const next = JSON.parse(JSON.stringify(filters || {}));
  const mapArray = (key: string, mapper: (item: any) => any) => {
    if (Array.isArray(next[key])) {
      next[key] = next[key].map(mapper);
    }
  };
  mapArray("locations", optionValue);
  mapArray("departments", optionValue);
  mapArray("teams", optionValue);
  mapArray("taskStatus", (item) => item?.id ?? item);
  mapArray("controlPriority", optionValue);
  mapArray("controlFrequency", optionValue);
  mapArray("taskType", (item) => item?.id ?? item);
  mapArray("framework", (item) => item?.label ?? item);
  mapArray("findings", optionValue);
  mapArray("activity", optionValue);
  mapArray("system", optionValue);
  mapArray("project", optionValue);
  if (Number(next.tier) === 10107) {
    delete next.system;
    delete next.project;
  }
  if (Number(next.tier) === 10108) {
    delete next.activity;
    delete next.project;
  }
  if (Number(next.tier) === 10109) {
    delete next.activity;
    delete next.system;
  }
  if (next.timePeriod !== undefined && next.timePeriod !== "") {
    next.timePeriod = Number(next.timePeriod);
  }
  return next;
};

const FILTER_LABELS: Record<string, string> = {
  timePeriod: "Time Period",
  locations: "Locations",
  departments: "Teams",
  teams: "Teams",
  taskStatus: "Status",
  taskType: "Task Type",
  control: "CID",
  controlPriority: "Priority",
  controlFrequency: "Frequency",
  framework: "Framework",
  tier: "Tier",
  activity: "Activities",
  system: "Systems",
  project: "Projects",
  findings: "Findings",
  kpiId: "KPI",
};

const PERIOD_LABELS: Record<string, string> = {
  "20601": "This Year",
  "20602": "Last Year",
  "20603": "Past 6 Months",
  "20604": "Past 3 Months",
};

const TIER_LABELS: Record<string, string> = {
  "10107": "Activity",
  "10108": "System",
  "10109": "Project",
};

const CHIP_VISIBLE_ITEMS = 2;

type FilterLookup = Array<{ value?: any; id?: any; label?: string; key?: string }>;

const lookupLabel = (value: any, options: FilterLookup = []) => {
  const match = options.find(
    (item) => String(item.value ?? item.id) === String(value)
  );
  return match?.label || match?.key || "";
};

const itemLabel = (item: any) => {
  if (item && typeof item === "object") {
    return item.label || item.key || item.value || "";
  }
  return item;
};

const formatChipValue = (
  key: string,
  value: any,
  lookups: Record<string, FilterLookup> = {}
) => {
  if (key === "timePeriod") {
    return PERIOD_LABELS[String(value)] || String(value);
  }
  if (key === "tier") {
    return TIER_LABELS[String(value)] || String(value);
  }
  if (Array.isArray(value)) {
    return value.map(itemLabel).filter(Boolean).join(", ");
  }
  if (value && typeof value === "object") {
    return value.label || value.key || value.value || "";
  }
  return lookupLabel(value, lookups[key]) || String(value);
};

const truncateChipValue = (fullValue: string) => {
  const parts = String(fullValue || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= CHIP_VISIBLE_ITEMS) {
    return fullValue;
  }
  return `${parts.slice(0, CHIP_VISIBLE_ITEMS).join(", ")} +${parts.length - CHIP_VISIBLE_ITEMS}`;
};

export type FilterChip = { key: string; label: string; value: string; fullValue: string };

export const buildFilterChips = (
  filters: Record<string, any> = {},
  lookups: Record<string, FilterLookup> = {}
): FilterChip[] => {
  return Object.entries(pickNonEmpty(filters))
    .map(([key, value]) => {
      const fullValue = formatChipValue(key, value, lookups);
      return {
        key,
        label: FILTER_LABELS[key] || key,
        value: truncateChipValue(fullValue),
        fullValue,
      };
    })
    .filter((chip) => Boolean(chip.fullValue));
};
